/* ============================================================
   ORGANISATION DASHBOARD — device-side roster cache + result submission.

   This browser app is the "no extra hardware" device mode of the org
   fit-for-work dashboard. The tablet is tied to a DEVICE API KEY (entered
   once in Device Setup, stored in localStorage) and talks to the dashboard
   through the same-origin relay under /api/dashboard/* (avoids CORS; the
   key travels in the x-device-key header, the server holds no key).

   Roster sync (contract):
   - GET  workers            → full roster first time, deltas via updatedSince
   - workerIds is ALWAYS the full list → delete local records not in it
   - photo fetched when a worker is new / updatedAt changed → face
     descriptor computed locally (faceid.js) and cached in IndexedDB
     along with the photo blob, so matching + display need no network.
   ============================================================ */
import { descriptorFromBlob, distance, DETECTOR_VERSION, MATCH_THRESHOLD } from "./faceid";

const LS_KEY = "onehealth_device_key";
/* `/site/{device_id}` uses a route-scoped credential without overwriting the
   tablet's saved Settings value. Module memory intentionally lasts only for
   this page lifetime and all existing roster/dashboard helpers read through
   getDeviceKey(), so they automatically share the active route identity. */
let runtimeDeviceKey = null;
export const setRuntimeDeviceKey = (k) => {
  const next = String(k || "").trim();
  runtimeDeviceKey = next || null;
};
export const clearRuntimeDeviceKey = () => { runtimeDeviceKey = null; };
export const getDeviceKey = () => {
  if (runtimeDeviceKey) return runtimeDeviceKey;
  try { return localStorage.getItem(LS_KEY) || ""; } catch (_) { return ""; }
};
export const setDeviceKey = (k) => {
  try { k && k.trim() ? localStorage.setItem(LS_KEY, k.trim()) : localStorage.removeItem(LS_KEY); } catch (_) {}
};

/* Keyless (site) mode — a kiosk without a device key picks its site after Get
   Started; roster access is read-only and scoped by the x-site-id header.
   Registration/onboarding stays device-key-only. */
const LS_SITE = "onehealth_site_id";
export const getSiteId = () => { try { return localStorage.getItem(LS_SITE) || ""; } catch (_) { return ""; } };
export const setSiteId = (id) => {
  try { id ? localStorage.setItem(LS_SITE, String(id)) : localStorage.removeItem(LS_SITE); } catch (_) {}
};
/* Any roster access at all (device key or selected site)? */
export const hasRosterAccess = () => !!(getDeviceKey() || getSiteId());

/* ── IndexedDB (workers + meta) ──
   A separate database is used for every credential/site identity. The suffix is
   a one-way, non-secret namespace fingerprint (never the credential itself).
   This keeps concurrent `/site/A` and `/site/B` tabs from clearing, reading or
   face-matching against each other's workers. */
function scopeFingerprint(value) {
  let a = 0x811c9dc5, b = 0x9e3779b9;
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b ^ c, 0x85ebca6b);
  }
  return `${(a >>> 0).toString(36)}${(b >>> 0).toString(36)}`;
}
function activeRosterScope() {
  if (runtimeDeviceKey) return `runtime:${runtimeDeviceKey}`;
  const deviceKey = getDeviceKey();
  if (deviceKey) return `device:${deviceKey}`;
  const siteId = getSiteId();
  return siteId ? `site:${siteId}` : "unlinked";
}
function captureRosterContext() {
  const key = getDeviceKey();
  const siteId = key ? "" : getSiteId();
  const scope = activeRosterScope();
  const headers = { Accept: "application/json" };
  if (key) headers["x-device-key"] = key;
  else if (siteId) headers["x-site-id"] = siteId;
  return {
    scope,
    key,
    siteId,
    headers,
    dbName: `onehealth-roster-${scopeFingerprint(scope)}`,
  };
}
function assertCurrentContext(ctx) {
  if (ctx.scope !== activeRosterScope()) throw new Error("Roster identity changed during operation");
}
function openDb(dbName = captureRosterContext().dbName) {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(dbName, 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains("workers")) db.createObjectStore("workers", { keyPath: "id" });
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta");
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
function idb(store, mode, run, ctx = captureRosterContext()) {
  return openDb(ctx.dbName).then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    let out;
    const req = run(t.objectStore(store));
    if (req && "onsuccess" in req) req.onsuccess = () => { out = req.result; };
    t.oncomplete = () => { db.close(); resolve(out); };
    t.onerror = () => { db.close(); reject(t.error); };
    t.onabort = () => { db.close(); reject(t.error); };
  }));
}
export const getWorkers = (ctx) => idb("workers", "readonly", (s) => s.getAll(), ctx).then((w) => w || []);
const workerPut = (rec, ctx) => idb("workers", "readwrite", (s) => s.put(rec), ctx);
const workerDelete = (id, ctx) => idb("workers", "readwrite", (s) => s.delete(id), ctx);
const metaGet = (k, ctx) => idb("meta", "readonly", (s) => s.get(k), ctx);
const metaPut = (k, v, ctx) => idb("meta", "readwrite", (s) => s.put(v, k), ctx);

/* ── Dashboard relay calls ── */
const hdrs = (ctx = captureRosterContext()) => ({ ...ctx.headers });

/* Public site list (no auth) — for the keyless site-selection step. */
export async function listSites() {
  const j = await fetchJSON("/api/dashboard/sites", { headers: { Accept: "application/json" } });
  const sites = (j?.sites || j || []);
  return (Array.isArray(sites) ? sites : []).map((s) => ({
    siteId: s.siteId ?? s.id,
    name: s.name || s.siteName || `Site ${s.siteId ?? s.id}`,
  })).filter((s) => s.siteId != null);
}

async function fetchJSON(url, opts) {
  const r = await fetch(url, opts);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`dashboard ${r.status}`);
  return r.json();
}

/* Download + locally embed one worker's enrollment photo. Mutates rec.
   Returns true (embedded), false (photo has no detectable face — final), or
   "retry" (network/photo unavailable — flagged for retry on later syncs). */
async function embedWorkerPhoto(rec, ctx) {
  try {
    assertCurrentContext(ctx);
    const pr = await fetch(`/api/dashboard/worker-photo/${encodeURIComponent(rec.id)}`, { headers: hdrs(ctx) });
    assertCurrentContext(ctx);
    if (!pr.ok) { rec.photoRetry = true; return "retry"; }
    rec.photoBlob = await pr.blob();
    assertCurrentContext(ctx);
    const d = await descriptorFromBlob(rec.photoBlob).catch(() => null);
    assertCurrentContext(ctx);
    rec.descriptor = d ? Array.from(d) : null;
    rec.detVer = DETECTOR_VERSION; // "no face" is terminal for this detector version
    rec.photoRetry = false;
    return !!rec.descriptor;
  } catch (_) {
    rec.photoRetry = true;
    return "retry";
  }
}

/* Wipe the local roster cache + sync cursor (device key rotation = identity
   boundary: another organisation's photos/embeddings must never linger). */
export async function clearRoster() {
  const ctx = captureRosterContext();
  await idb("workers", "readwrite", (s) => s.clear(), ctx);
  await idb("meta", "readwrite", (s) => s.clear(), ctx);
}

/* Remove a legacy identity cache without opening/creating a replacement DB.
   Used by phone journeys that must leave no persistent roster store. */
export function deleteRosterCache() {
  const { dbName } = captureRosterContext();
  return new Promise((resolve, reject) => {
    const r = indexedDB.deleteDatabase(dbName);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
    r.onblocked = () => reject(new Error("Roster cache is busy"));
  });
}

/* Sync the roster. force=true refetches everything (full resync + re-embed). */
const activeRosterSyncs = new Set();
export function waitForRosterIdle() {
  return Promise.allSettled(Array.from(activeRosterSyncs));
}
export function syncRoster(options = {}) {
  const task = syncRosterInner(options);
  activeRosterSyncs.add(task);
  task.then(() => activeRosterSyncs.delete(task), () => activeRosterSyncs.delete(task));
  return task;
}
async function syncRosterInner({ force = false, onProgress } = {}) {
  const ctx = captureRosterContext();
  if (!ctx.key && !ctx.siteId) throw new Error("No device key or site configured");
  const since = force ? null : await metaGet("serverTime", ctx);
  assertCurrentContext(ctx);
  const j = await fetchJSON(
    "/api/dashboard/workers" + (since ? `?updatedSince=${encodeURIComponent(since)}` : ""),
    { headers: hdrs(ctx) }
  );
  assertCurrentContext(ctx);
  if (!j) throw new Error("roster endpoint not found");

  const keepIds = new Set((j.workerIds || []).map(Number));
  const existing = await getWorkers(ctx);
  assertCurrentContext(ctx);
  for (const w of existing) {
    if (!keepIds.has(Number(w.id))) {
      await workerDelete(w.id, ctx);
      assertCurrentContext(ctx);
    }
  }
  const prevById = new Map(existing.map((w) => [Number(w.id), w]));

  const changed = j.workers || [];
  let embedded = 0, noFace = 0;
  for (let i = 0; i < changed.length; i++) {
    const w = changed[i];
    const prev = prevById.get(Number(w.id));
    const stale = force || !prev || prev.updatedAt !== w.updatedAt;
    const rec = {
      ...w,
      // A changed worker's OLD photo/descriptor is superseded enrollment data —
      // never carry it forward. If the new photo can't be fetched/embedded the
      // fields stay null and photoRetry marks it for retry on later syncs.
      photoBlob: stale ? null : prev?.photoBlob || null,
      descriptor: stale ? null : prev?.descriptor || null,
      photoRetry: false,
    };
    if (stale || !rec.descriptor) {
      const got = await embedWorkerPhoto(rec, ctx);
      got === true ? embedded++ : noFace++;
    }
    assertCurrentContext(ctx);
    await workerPut(rec, ctx);
    assertCurrentContext(ctx);
    onProgress?.({ done: i + 1, total: changed.length });
  }

  // Retry workers whose photo fetch previously failed — delta responses won't
  // include them again, so they'd otherwise stay unmatched forever. Also
  // re-embed cached "no face" photos exactly once per detector version bump
  // (detVer marks the outcome as terminal for that version — no endless
  // re-inference of genuinely faceless photos on every sync).
  const changedIds = new Set(changed.map((w) => Number(w.id)));
  for (const w of await getWorkers(ctx)) {
    assertCurrentContext(ctx);
    if (changedIds.has(Number(w.id))) continue;
    if (w.photoRetry) {
      const got = await embedWorkerPhoto(w, ctx);
      if (got === true) embedded++;
      await workerPut(w, ctx);
      assertCurrentContext(ctx);
    } else if (w.photoBlob && !w.descriptor && w.detVer !== DETECTOR_VERSION) {
      const d = await descriptorFromBlob(w.photoBlob).catch(() => null);
      assertCurrentContext(ctx);
      w.descriptor = d ? Array.from(d) : null;
      w.detVer = DETECTOR_VERSION;
      if (d) embedded++;
      await workerPut(w, ctx);
      assertCurrentContext(ctx);
    }
  }

  if (j.serverTime) await metaPut("serverTime", j.serverTime, ctx);
  assertCurrentContext(ctx);
  await metaPut("lastSync", new Date().toISOString(), ctx);
  assertCurrentContext(ctx);
  const total = (await getWorkers(ctx)).length;
  assertCurrentContext(ctx);
  return { total, updated: changed.length, embedded, noFace, serverTime: j.serverTime };
}

export async function rosterStatus() {
  const ws = await getWorkers();
  return {
    total: ws.length,
    embedded: ws.filter((w) => w.descriptor).length,
    lastSync: (await metaGet("lastSync")) || null,
  };
}

/* Site + department list from the dashboard (GET /api/v1/device/departments).
   Each department carries its enabled assessment list; the flags below map the
   ones this kiosk can run (temperature/alcohol need gate hardware). */
const hasAssess = (list, ...names) => Array.isArray(list) && list.some((a) => names.includes(String(a).toLowerCase()));
export async function listDepartments() {
  const j = await fetchJSON("/api/dashboard/departments", { headers: hdrs() });
  if (!j) return { siteName: "", departments: [] };
  const departments = (j.departments || []).map((d) => ({
    departmentId: d.id,
    name: d.name || `Department ${d.id}`,
    assessments: d.assessments || [],
    pulmoRequired: hasAssess(d.assessments, "pulmo_scan", "pulmo"),
    cognitiveRequired: hasAssess(d.assessments, "cognitive", "cognitive_test", "attention"),
  }));
  return { siteId: j.siteId, siteName: j.siteName || "", departments };
}

/* Onboard a brand-new worker at the kiosk (POST /api/v1/device/workers).
   The dashboard validates the department belongs to the device's site and
   returns the created worker incl. pulmoRequired/cognitiveRequired. */
export function onboardWorker(payload) {
  return fetchJSON("/api/dashboard/onboard-worker", {
    method: "POST",
    headers: { ...hdrs(), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then(unwrapWorkerResponse);
}

/* Best local match for a live-frame descriptor, or null. */
export async function matchWorker(desc) {
  const ws = await getWorkers();
  let best = null, bestD = Infinity;
  for (const w of ws) {
    if (!w.descriptor) continue;
    const d = distance(desc, new Float32Array(w.descriptor));
    if (d < bestD) { bestD = d; best = w; }
  }
  return best && bestD <= MATCH_THRESHOLD ? { worker: best, distance: bestD } : null;
}

/* Manual employee-ID entry → verify-worker (returns worker or null on 404). */
function unwrapWorkerResponse(response) {
  if (!response || response.valid === false) return null;
  return response.worker || response.data?.worker || response;
}

export function verifyWorker(employeeId) {
  return fetchJSON("/api/dashboard/verify-worker", {
    method: "POST",
    cache: "no-store",
    headers: { ...hdrs(), "Content-Type": "application/json" },
    body: JSON.stringify({ employeeId }),
  }).then(unwrapWorkerResponse);
}

/* Fetch one enrollment photo for an online, one-to-one identity check.
   The caller owns the Blob; this helper never writes it to IndexedDB. */
export async function fetchWorkerPhoto(workerId) {
  const r = await fetch(`/api/dashboard/worker-photo/${encodeURIComponent(workerId)}?ephemeral=1`, {
    headers: hdrs(),
    cache: "no-store",
  });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`dashboard ${r.status}`);
  return r.blob();
}

/* Submit the completed screening to the dashboard. */
export function submitResults(payload) {
  return fetchJSON("/api/dashboard/results", {
    method: "POST",
    headers: { ...hdrs(), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}
